import { PricesRangeWithTotal } from '../types';

/**
 * Extracts the discount value for a specific price field if applicable
 *
 * @param key - Price field identifier ('base', 'price', 'obFees', 'total')
 * @param price - Original price object containing standard prices
 * @param discountPrices - Optional object containing discount prices
 *
 * @returns {number | undefined}
 * - The discount price if available
 * - undefined if:
 *   - Original price field is undefined
 *   - Discount price field is undefined
 *
 * @example
 * ```ts
 * const original = {
 *   base: 100,
 *   price: 50,
 *   total: 300
 * };
 * const discounted = {
 *   base: 80,
 *   price: 40,
 *   total: 240
 * };
 *
 * getDiscountField('base', original, discounted);
 * // Returns: 80
 *
 * getDiscountField('obFees', original, discounted);
 * // Returns: undefined (field not present in original)
 * ```
 */
type PriceKey = keyof Pick<
  PricesRangeWithTotal,
  'base' | 'price' | 'obFees' | 'total'
>;

export const getDiscountField = (
  key: PriceKey,
  price: Partial<PricesRangeWithTotal>,
  discountPrices?: PricesRangeWithTotal,
): number | undefined => {
  if (price?.[key] === undefined) return;

  if (discountPrices?.[key] !== undefined) {
    return discountPrices[key];
  }

  return;
};
